import { useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Box, CircularProgress, Divider, Grid, Paper, Typography } from '@mui/material';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { SearchPanel } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/components/Filters/SearchPanel.tsx';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { getCovenantsRequestErrorMessage } from '~/modules/covenants/utils.ts';
import {
  SUPERVISOR_ADMINISTRATION_ARIA,
  SUPERVISOR_ADMINISTRATION_ERRORS,
  SUPERVISOR_ADMINISTRATION_TEXT,
} from './constants.ts';
import { supervisorAdministrationStyles } from './styles.ts';
import SupervisorAdministrationCard from './SupervisorAdministrationCard.tsx';
import type { TSupervisorAdministrationSection } from './types.ts';
import useSupervisorAdministrationForm from './useSupervisorAdministrationForm.ts';
import {
  buildSupervisorAdministrationEmailFieldName,
  hasDuplicateSupervisorAdministrationEmail,
} from './utils.ts';

type TSupervisorAdministrationPageProps = {
  titleSuffix: string;
  subtitle: string;
  sections: TSupervisorAdministrationSection[];
  isLoading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  onAddUser?: (sectionId: string, email: string) => Promise<void> | void;
  onDeleteUser?: (sectionId: string, userId: string) => Promise<void> | void;
  onSearchSubmit?: (email: string) => Promise<void> | void;
  useServerSearch?: boolean;
  addingSectionId?: string | null;
  deletingUserId?: string | null;
};

const filterSections = (
  sections: TSupervisorAdministrationSection[],
  searchQuery: string
): TSupervisorAdministrationSection[] => {
  const normalizedQuery = searchQuery.trim().toLowerCase();

  if (!normalizedQuery) {
    return sections;
  }

  return sections.filter(
    (section) =>
      section.id.toLowerCase().includes(normalizedQuery) ||
      section.title.toLowerCase().includes(normalizedQuery) ||
      section.users.some((user) => user.email.toLowerCase().includes(normalizedQuery))
  );
};

const SupervisorAdministrationPage = ({
  titleSuffix,
  subtitle,
  sections,
  isLoading = false,
  emptyTitle = SUPERVISOR_ADMINISTRATION_TEXT.emptyTitle,
  emptyDescription = SUPERVISOR_ADMINISTRATION_TEXT.emptyDescription,
  onAddUser,
  onDeleteUser,
  onSearchSubmit,
  useServerSearch = false,
  addingSectionId = null,
  deletingUserId = null,
}: TSupervisorAdministrationPageProps) => {
  const formMethods = useSupervisorAdministrationForm();
  const [localSections, setLocalSections] = useState(sections);
  const [modalErrorText, setModalErrorText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [submittedSearchQuery, setSubmittedSearchQuery] = useState('');
  const effectiveSections = onAddUser || onDeleteUser ? sections : localSections;
  const visibleSections = useMemo(
    () =>
      useServerSearch
        ? effectiveSections
        : filterSections(effectiveSections, submittedSearchQuery),
    [effectiveSections, submittedSearchQuery, useServerSearch]
  );

  const handleAddUser = async (sectionId: string) => {
    const fieldName = buildSupervisorAdministrationEmailFieldName(sectionId);
    const isFieldValid = await formMethods.trigger(fieldName);
    const email = formMethods.getValues(fieldName)?.trim() ?? '';
    const section = effectiveSections.find((item) => item.id === sectionId);

    if (!email || !isFieldValid) {
      return;
    }

    if (hasDuplicateSupervisorAdministrationEmail(section?.users ?? [], email)) {
      formMethods.setError(fieldName, {
        type: 'duplicate',
        message: SUPERVISOR_ADMINISTRATION_ERRORS.duplicateEmail,
      });
      return;
    }

    if (onAddUser) {
      try {
        await onAddUser(sectionId, email);
      } catch (error) {
        setModalErrorText(getCovenantsRequestErrorMessage(error));
        return;
      }
    } else {
      setLocalSections((currentSections) =>
        currentSections.map((currentSection) =>
          currentSection.id === sectionId
            ? {
                ...currentSection,
                users: [
                  ...currentSection.users,
                  {
                    id: `${sectionId}-${Date.now()}`,
                    email,
                  },
                ],
              }
            : currentSection
        )
      );
    }

    formMethods.setValue(fieldName, '', {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    formMethods.clearErrors(fieldName);
  };

  const handleDeleteUser = async (sectionId: string, userId: string) => {
    if (onDeleteUser) {
      try {
        await onDeleteUser(sectionId, userId);
      } catch (error) {
        setModalErrorText(getCovenantsRequestErrorMessage(error));
      }

      return;
    }

    setLocalSections((currentSections) =>
      currentSections.map((currentSection) =>
        currentSection.id === sectionId
          ? {
              ...currentSection,
              users: currentSection.users.filter((user) => user.id !== userId),
            }
          : currentSection
      )
    );
  };

  if (isLoading) {
    return (
      <Box sx={commonStyles.centeredLoader}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <Box sx={commonStyles.pageRoot}>
        <Box sx={supervisorAdministrationStyles.pageContent}>
          <Box sx={commonStyles.header}>
            <Box>
              <Typography variant="h4" sx={commonStyles.title}>
                {`${SUPERVISOR_ADMINISTRATION_TEXT.administrationTitlePrefix} ${titleSuffix}`}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={commonStyles.subtitle}>
                {subtitle}
              </Typography>
            </Box>
          </Box>
          <Divider sx={supervisorAdministrationStyles.pageDivider} />

          <Box sx={commonStyles.content}>
            <SearchPanel
              searchQuery={searchQuery}
              placeholder={SUPERVISOR_ADMINISTRATION_TEXT.searchPlaceholder}
              searchButtonAriaLabel={SUPERVISOR_ADMINISTRATION_ARIA.searchButton}
              clearButtonAriaLabel={SUPERVISOR_ADMINISTRATION_ARIA.clearSearchButton}
              autoComplete="off"
              onSearchChange={(nextSearchQuery) => {
                setSearchQuery(nextSearchQuery);

                if (!nextSearchQuery.length) {
                  setSubmittedSearchQuery('');
                  void Promise.resolve(onSearchSubmit?.('')).catch((error) => {
                    setModalErrorText(getCovenantsRequestErrorMessage(error));
                  });
                }
              }}
              onSearchSubmit={() => {
                const nextSubmittedSearchQuery = searchQuery.trim();

                setSubmittedSearchQuery(nextSubmittedSearchQuery);
                void Promise.resolve(onSearchSubmit?.(nextSubmittedSearchQuery)).catch((error) => {
                  setModalErrorText(getCovenantsRequestErrorMessage(error));
                });
              }}
            />

            {!!visibleSections.length ? (
              <Grid container spacing={3}>
                {visibleSections.map((section) => (
                  <Grid key={section.id} size={{ xs: 12, md: 6, xl: 4 }}>
                    <SupervisorAdministrationCard
                      id={section.id}
                      title={section.title}
                      subtitleRows={[
                        `${SUPERVISOR_ADMINISTRATION_TEXT.guaranteesRolePrefix}: ${section.title}`,
                        `${SUPERVISOR_ADMINISTRATION_TEXT.guaranteesUsersCountPrefix}: ${section.users.length}`,
                      ]}
                      users={section.users}
                      fieldName={buildSupervisorAdministrationEmailFieldName(section.id)}
                      isAdding={addingSectionId === section.id}
                      deletingUserId={deletingUserId}
                      onAddUser={(sectionId) => {
                        void handleAddUser(sectionId);
                      }}
                      onDeleteUser={(sectionId, userId) => {
                        void handleDeleteUser(sectionId, userId);
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper elevation={0} sx={supervisorAdministrationStyles.emptyPaper}>
                <Typography variant="h6" gutterBottom>
                  {emptyTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {emptyDescription}
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>

        <ModalAlert
          isOpen={!!modalErrorText}
          text={modalErrorText}
          onClose={() => setModalErrorText('')}
        />
      </Box>
    </FormProvider>
  );
};

export default SupervisorAdministrationPage;
